/**
 * Cache dla statusów użytkownika na kartach eventów
 * Używa localStorage do przechowywania danych między sesjami
 */

const CACHE_KEY = 'eventCardStatusCache';
const CACHE_TTL = 1000 * 60 * 30; // 30 minut TTL

/**
 * Pobierz cały cache z localStorage
 */
const getCache = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return {};
    return JSON.parse(cached);
  } catch {
    return {};
  }
};

/**
 * Zapisz cache do localStorage
 */
const saveCache = (cache) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (err) {
    console.warn('Failed to save event card cache:', err);
  }
};

/**
 * Generuj klucz cache dla danego użytkownika i eventu
 */
const getCacheKey = (userId, eventId) => `${userId}_${eventId}`;

/**
 * Pobierz cached status dla eventu
 * @param {string} userId - ID użytkownika
 * @param {string} eventId - ID eventu
 * @returns {Object|null} - Cached data lub null jeśli brak/wygasł
 */
export const getCachedEventStatus = (userId, eventId) => {
  if (!userId || !eventId) return null;
  
  const cache = getCache();
  const key = getCacheKey(userId, eventId);
  const entry = cache[key];
  
  if (!entry) return null;
  
  // Sprawdź czy cache nie wygasł
  if (Date.now() - entry.timestamp > CACHE_TTL) {
    // Usuń wygasły wpis
    delete cache[key];
    saveCache(cache);
    return null;
  }
  
  return entry.data;
};

/**
 * Zapisz status eventu do cache
 * @param {string} userId - ID użytkownika
 * @param {string} eventId - ID eventu
 * @param {Object} data - Dane do zapisania
 */
export const setCachedEventStatus = (userId, eventId, data) => {
  if (!userId || !eventId) return;
  
  const cache = getCache();
  const key = getCacheKey(userId, eventId);
  
  cache[key] = {
    data,
    timestamp: Date.now()
  };
  
  // Ogranicz rozmiar cache - usuń najstarsze wpisy jeśli przekroczono limit
  const entries = Object.entries(cache);
  if (entries.length > 100) {
    // Sortuj po timestamp i usuń najstarsze
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    const toRemove = entries.slice(0, entries.length - 100);
    toRemove.forEach(([k]) => delete cache[k]);
  }
  
  saveCache(cache);
};

/**
 * Wyczyść cache dla konkretnego eventu (np. po zmianie statusu)
 * @param {string} eventId - ID eventu
 */
export const invalidateEventCache = (eventId) => {
  if (!eventId) return;
  
  const cache = getCache();
  const keysToRemove = Object.keys(cache).filter(key => key.endsWith(`_${eventId}`));
  keysToRemove.forEach(key => delete cache[key]);
  saveCache(cache);
};

/**
 * Wyczyść cały cache użytkownika (np. po wylogowaniu)
 * @param {string} userId - ID użytkownika
 */
export const clearUserCache = (userId) => {
  if (!userId) return;
  
  const cache = getCache();
  const keysToRemove = Object.keys(cache).filter(key => key.startsWith(`${userId}_`));
  keysToRemove.forEach(key => delete cache[key]);
  saveCache(cache);
};

/**
 * Wyczyść cały cache
 */
export const clearAllCache = () => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (err) {
    console.warn('Failed to clear event card cache:', err);
  }
};
